/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Dialog, DialogTitle, DialogContent } from "@mui/material";
import React, { useContext } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as yup from "yup";
import { addEntity } from "../../lib/services/api-service";
import { TableName } from "../../constants/table";
import { useGetAllCompanyMasters } from "../../lib/query/companyMaster";
import { ICompanyMaster, ICompanyMapping } from "../../models/companyMaster";
import TenantContext from "../../lib/context/TenantContext";
import { FormInputText } from "../formComponent/FormInputText";
import { yupResolver } from "@hookform/resolvers/yup";
import { v4 } from "uuid";

const AddCompanyMaster = (props: {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { tenant } = useContext(TenantContext);
  const { data: companyMasters, refetch } = useGetAllCompanyMasters(tenant.id);

  const schema = yup
    .object()
    .shape({
      CompanyOCR: yup.string().required("Company OCR is required"),
      CompanyName: yup.string().required("Company Name is required"),
    })
    .required();

  const { handleSubmit, control, reset } = useForm<
    ICompanyMaster & ICompanyMapping
  >({
    defaultValues: {
      Active: false,
      IsSystemGenerated: false,
    },
    resolver: yupResolver(schema) as any,
  });

  const onSubmit = async (data: ICompanyMaster & ICompanyMapping) => {
    if (tenant.companyLicenses !== null && companyMasters) {
      const activeCompanies = companyMasters.filter((row) => row.Active).length;
      const remainingCompanyLicenses = tenant.companyLicenses - activeCompanies;

      if (data.Active && remainingCompanyLicenses <= 0) {
        toast.warn(
          "Company licenses have reached the limit for active companies."
        );
        return;
      }
    }

    const partitionKey = tenant.id.toLowerCase();
    const companyMasterRowKey = data.CompanyGst
      ? `GST_${data.CompanyGst.trim()}`
      : v4();
    const companyMappingRowKey = v4();
    const isSystemGenerated = false;

    try {
      // Add to CompanyMaster
      await addEntity(TableName.CompanyMaster, {
        partitionKey,
        rowKey: companyMasterRowKey,
        IsSystemGenerated: isSystemGenerated,
        Active: data.Active,
        CompanyName: data.CompanyName,
        CompanyGst: data.CompanyGst,
        ExportFormat: 1, // Default value
      });

      // Add to CompanyMapping
      await addEntity(TableName.CompanyMapping, {
        partitionKey,
        rowKey: companyMappingRowKey,
        CompanyOCR: data.CompanyOCR,
        CompanyId: companyMasterRowKey,
        Active: data.Active,
        IsSystemGenerated: isSystemGenerated,
      });

      toast.success("Successfully added company.");
      refetch();
      props.setShowDialog(false);
      reset();
    } catch (error) {
      toast.warn("Error adding company. It may already exist.");
    }
  };

  const handleClose = () => {
    props.setShowDialog(false);
    reset();
  };

  return (
    <React.Fragment>
      <Dialog open={props.showDialog} onClose={handleClose}>
        <DialogTitle>
          <div className=" text-xl">ADD COMPANY MASTER</div>
        </DialogTitle>

        <DialogContent sx={{ width: "400px" }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mt-3">
              <FormInputText
                name="CompanyOCR"
                control={control}
                label="Company OCR"
              />
            </div>
            <div className="mt-4">
              <FormInputText
                name="CompanyName"
                control={control}
                label="Company Name"
              />
            </div>
            <div className="mt-4">
              <FormInputText
                name="CompanyGst"
                control={control}
                label="Company GST"
              />
            </div>
            <div className="mt-6 flex items-end justify-end space-x-4">
              <Button onClick={handleClose} variant="contained">
                Discard
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Add
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default AddCompanyMaster;
